import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Input as RInput } from "reactstrap";
import { NavLink as RRNavLink } from "react-router-dom";
import cx from "classnames";
import PropTypes from "prop-types";

import global from "../../../scss/dhp.scss";

import { Icon } from "../../ui/icon";
import { Input } from "../../ui/input";
import { preventFormSubmitOnEnter } from "../../../_helpers/utils";
import { fetchProposalModuleList } from "../../../store/actions/companyActions";
import { addSigner, getSignerDetails, updateSignerDetails } from "../../../store/actions/editorActions";
import { useCheckCompanyPlanInfo } from "../../../hooks/useCheckCompanyPlanInfo";
import { COMPANY_SUPERADMIN } from "../../../constants/company";

let style = Object.assign({}, global);

const SignatureModal = props => {
  const dispatch = useDispatch();

  const { proposalModuleListInfo } = useSelector(state => state.company);
  const documentId = useSelector(state => state.document?.documentDetails?.data?.id);
  const maxLength = 100;

  const [signatureDetails, setSignatureDetails] = useState();
  const [signatureName, setSignatureName] = useState();
  const [signatureEmail, setSignatureEmail] = useState();
  const [isEmailValid, setIsEmailValid] = useState();
  const [isUseSignatureFormEnabled, setIsUseSignatureFormEnabled] = useState();
  const [signatureFormOptions, setSignatureFormOptions] = useState();
  const [activeSignatureFormOption, setActiveSignatureFormOption] = useState();
  const [activeSignatureFormOptionId, setActiveSignatureFormOptionId] = useState();
  const [isFormValid, setIsFormValid] = useState(false);
  const [loadCurrentValue, setLoadCurrentValue] = useState();

  // check is this company is paid or not
  const paidCompanyInfo = useCheckCompanyPlanInfo(true);
  const isSuperAdmin = paidCompanyInfo?.companyRole === COMPANY_SUPERADMIN;
  const TooltipText = isSuperAdmin ? "Manage Signature Form" : "Only Admin can manageSignature Form";
  const fetch = {
    page: 1,
    limit: 25,
  };

  const closeModal = () => {
    props.setShowModal(!props.showModal);
  };

  //Check email validation onchange
  const checkEmailValidity = e => {
    if (e.target.value.length === 0) {
      setIsEmailValid(false);
      setSignatureEmail("");
    }
    //Check validation when start typing
    else {
      let regx = new RegExp(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,15}/g);
      let duplicateEmaillist = props.availableEmailList ? props.availableEmailList : [];

      // modifie duplicate id list at time of update
      if (props.activeSignatureId)
        duplicateEmaillist = props.availableEmailList.filter(email => email !== signatureDetails.email);

      //if valid both email type and duplicate email
      if (regx.test(e.target.value.trim()) && !duplicateEmaillist.includes(e.target.value.trim())) {
        setIsEmailValid(true);
        setSignatureEmail(e.target.value.trim());
      }
      //if not valid
      else {
        setIsEmailValid(false);
        setSignatureEmail(e.target.value.trim());
      }
    }
  };

  // change signer form value
  const updateSignatureFormValue = option => {
    setLoadCurrentValue(false);
    setActiveSignatureFormOption({ name: option.name });
    setActiveSignatureFormOptionId(option._id);
  };

  // save or update signer info
  const saveSigner = () => {
    setIsFormValid(false);
    let payload = {
      name: signatureName,
      email: signatureEmail,
      document: documentId,
      signature_form: isUseSignatureFormEnabled ? activeSignatureFormOptionId : {},
    };

    //Add signer
    if (!props.activeSignatureId) {
      if (isUseSignatureFormEnabled) {
        props.setTargetSignatureFormId({ inuse: true, form_id: activeSignatureFormOptionId });
      }
      dispatch(addSigner(payload)).then(r => {
        props.setIsFormSave(true);
        closeModal();
      });
    }
    //Update signer
    else {
      props.setTargetSignatureFormId({ inuse: isUseSignatureFormEnabled, form_id: activeSignatureFormOptionId });
      dispatch(updateSignerDetails(payload, props.activeSignatureId)).then(r => {
        props.setIsFormSave(true);
        closeModal();
      });
    }
  };

  // call 1st time after render
  useEffect(() => {
    dispatch(fetchProposalModuleList("signature_forms", fetch)); // fetch dropdown options for signature form

    // load current values during update modal
    if (props.activeSignatureId) {
      setLoadCurrentValue(true);
      dispatch(getSignerDetails(props.activeSignatureId, documentId)).then(r => {
        setSignatureDetails(r.data.data);
        setSignatureName(r.data.data.name);
        setSignatureEmail(r.data.data.email);
        setIsEmailValid(true);
        if (r.data.data?.signatureForm?.name) {
          setIsUseSignatureFormEnabled(true);
          setActiveSignatureFormOption({ name: r.data.data.signatureForm.name });
          setActiveSignatureFormOptionId(r.data.data.signatureForm._id);
        } else setIsUseSignatureFormEnabled(false);
      });
    }
  }, []);

  // set signature form dropdown options based on search result
  useEffect(() => {
    if (proposalModuleListInfo) setSignatureFormOptions(proposalModuleListInfo?.signature_forms?.data);
  }, [proposalModuleListInfo]);

  // set signature form dropdown value when check box clicked at add signature time
  useEffect(() => {
    if (isUseSignatureFormEnabled && !loadCurrentValue) {
      setActiveSignatureFormOption({ name: proposalModuleListInfo.signature_forms?.data[0]?.name });
      setActiveSignatureFormOptionId(proposalModuleListInfo.signature_forms?.data[0]?._id);
    }
  }, [isUseSignatureFormEnabled]);

  // check is form valid or not on any inputb value change
  useEffect(() => {
    if (loadCurrentValue) return;

    if (
      signatureName &&
      isEmailValid &&
      ((isUseSignatureFormEnabled && activeSignatureFormOption?.name) || !isUseSignatureFormEnabled)
    ) {
      setIsFormValid(true);
    } else setIsFormValid(false);
  }, [signatureName, isEmailValid, isUseSignatureFormEnabled, activeSignatureFormOption]);

  return (
    <>
      <span className={cx(style["cross-modal"], style["rounded"])} onClick={closeModal}>
        <Icon icon="ui-close" />
      </span>

      <Form id="signerForm" onKeyDown={e => preventFormSubmitOnEnter(e)}>
        <h4 className={cx(style["fw-7"], style["mb-4"])}>
          {props.activeSignatureId && <>Update Signer</>} {!props.activeSignatureId && <>Add Signer</>}
        </h4>

        {/* signature form name */}
        <Input
          label="Name"
          type="text"
          name="nameTextField"
          value={signatureName}
          maxLength={maxLength}
          onChange={e => {
            setLoadCurrentValue(false);
            setSignatureName(e.target.value);
          }}
          required={true}
          autoFocus
        />

        {/* signature form email */}
        <Input
          label="Email"
          type="text"
          name="emailTextField"
          value={signatureEmail}
          onChange={e => {
            setLoadCurrentValue(false);
            checkEmailValidity(e);
          }}
          required={true}
          autoFocus
        />

        {/* signature form check box */}
        <div
          className={cx(
            style["form-check"],
            style["form-switch"],
            style["mb-3"],
            style["d-flex"],
            style["align-items-center"]
          )}>
          <RInput
            type="checkbox"
            className={style["form-check-input"]}
            defaultChecked={isUseSignatureFormEnabled}
            id="customSwitchForSignature"
            value={isUseSignatureFormEnabled}
            onClick={() => {
              setLoadCurrentValue(false);
              setIsUseSignatureFormEnabled(!isUseSignatureFormEnabled);
            }}
          />

          <label className={style["form-check-label"]} htmlFor="customSwitchForSignature">
            Use Signature Form
          </label>
          <div className={cx(style["custom-tooltip"], style["ms-2"])}>
            <Icon icon="ui-info" additionalclass={"font-14"} />
            <div className={cx(style["custom-tooltip-content"], style["bottom"], style["text-center"], style["mt-n3"])}>
              Collect additional details <br></br> during signature
            </div>
          </div>
        </div>

        {/* signature form dropdown */}
        {isUseSignatureFormEnabled && (
          <div
            className={cx(
              style["d-flex"],
              style["justify-content-between"],
              style["mt-4"],
              style["select-manage-brand"]
            )}>
            <Input
              cssModule={style}
              formGroupClass={style["w-100"]}
              returnType="formGroup"
              type="dropdown"
              label="Signature Form"
              required={true}
              classNamePrefix="select"
              dropDownMenuClass="text-capitalize"
              className="input-with-link"
              getOptionValue={option => option?.name}
              getOptionLabel={option => <span>{option?.name}</span>}
              options={signatureFormOptions}
              updateState={option => updateSignatureFormValue(option)}
              defaultValue={activeSignatureFormOption}
              manageItemLink={`/companies/settings/signature-forms`}
              manageItemText={TooltipText}
              isSuperAdmin={isSuperAdmin}
            />
          </div>
        )}

        <Button type="button" color="primary" disabled={!isFormValid} onClick={saveSigner}>
          Save
        </Button>
      </Form>
    </>
  );
};

export default SignatureModal;
